import React, { useState } from 'react';
import { addDays, addYears, startOfYear, startOfMonth, format } from 'date-fns';
import { Calendar } from 'lucide-react';

const Lists: React.FC = () => {
  const [tab, setTab] = useState<'recent' | 'years' | 'big'>('recent');

  const now = new Date();

  // Recent / Upcoming
  const recentItems = Array.from({ length: 14 }).map((_, i) => {
    const d = addDays(now, i - 2); // Start from 2 days ago
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return {
      label: i === 2 ? 'Today' : format(d, 'EEEE, MMM do'),
      ts: Math.floor(start.getTime() / 1000),
      date: format(start, 'yyyy-MM-dd HH:mm:ss')
    };
  });

  // Yearly Grid (1930 - 2038)
  const years = Array.from({ length: 109 }).map((_, i) => {
    const y = 1930 + i;
    const d = startOfYear(new Date(y, 0, 1));
    return { year: y, ts: Math.floor(d.getTime() / 1000) };
  });

  // Big Numbers
  const bigNumbers = [
    { label: '1 Billion Seconds', ts: 1000000000, date: new Date(1000000000 * 1000).toUTCString() },
    { label: '1.5 Billion Seconds', ts: 1500000000, date: new Date(1500000000 * 1000).toUTCString() },
    { label: '1.7 Billion Seconds', ts: 1700000000, date: new Date(1700000000 * 1000).toUTCString() },
    { label: '2 Billion Seconds', ts: 2000000000, date: new Date(2000000000 * 1000).toUTCString() },
    { label: 'Max Int32 (Year 2038)', ts: 2147483647, date: new Date(2147483647 * 1000).toUTCString() },
  ];

  return (
    <div className="space-y-6">
      <div className="flex space-x-4 border-b border-slate-200 pb-2">
        <button 
          onClick={() => setTab('recent')}
          className={`px-4 py-2 font-medium text-sm rounded-lg transition-colors ${tab === 'recent' ? 'bg-sky-100 text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}
        >
          Recent & Upcoming
        </button>
        <button 
          onClick={() => setTab('years')}
          className={`px-4 py-2 font-medium text-sm rounded-lg transition-colors ${tab === 'years' ? 'bg-sky-100 text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}
        >
          Start of Year (1930-2038)
        </button>
        <button 
          onClick={() => setTab('big')}
          className={`px-4 py-2 font-medium text-sm rounded-lg transition-colors ${tab === 'big' ? 'bg-sky-100 text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}
        >
          Significant Epochs
        </button>
      </div>

      {tab === 'recent' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Day</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Timestamp (Midnight)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date String</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {recentItems.map((item, i) => (
                <tr key={i} className={item.label === 'Today' ? 'bg-sky-50' : 'hover:bg-slate-50'}>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.label}</td>
                  <td className="px-6 py-4 text-sm font-mono text-sky-600">{item.ts}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">{item.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'years' && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
           <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
             {years.map(y => (
               <div key={y.year} className="text-center p-2 border border-slate-100 rounded hover:bg-slate-50">
                 <div className="text-xs text-slate-400 font-bold">{y.year}</div>
                 <div className="text-sm font-mono text-slate-700">{y.ts}</div>
               </div>
             ))}
           </div>
        </div>
      )}

      {tab === 'big' && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="space-y-4">
            {bigNumbers.map((item, i) => (
              <div key={i} className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-slate-100 rounded-lg hover:bg-slate-50">
                <div>
                  <h3 className="font-bold text-slate-800">{item.label}</h3>
                  <p className="text-sm text-slate-500">{item.date}</p>
                </div>
                <div className="text-2xl font-mono text-sky-600 font-bold mt-2 md:mt-0">
                  {item.ts}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Lists;